import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getBoardClienteExibicao } from '../../board/utils/clienteBoardDisplay';

const API_BASE_URL = getApiBaseUrl();

let cachedStatus = null;
let cachedAt = 0;
const CACHE_MS = 60_000;

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

export function normalizeOsStatusCod(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 2);
}

/**
 * Status inicial Customers (LCMY_VC_SYS_CONFIG.os_status_inicial).
 * Cache curto — refresh leve.
 */
export async function fetchOsStatusInicial({ force = false } = {}) {
  const now = Date.now();
  if (!force && cachedStatus != null && now - cachedAt < CACHE_MS) {
    return cachedStatus;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/config/vc-sys-config/os-status-inicial`, {
      headers: authHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) return cachedStatus;
    const cod = normalizeOsStatusCod(json?.data?.codstatus);
    cachedStatus = cod.length === 2 ? cod : '';
    cachedAt = now;
    return cachedStatus;
  } catch {
    return cachedStatus;
  }
}

/**
 * Snapshot silencioso de Em aberto (GET /api/orders/open).
 * Poll de Novos chamados — sem spinner / log.
 * Suporta If-None-Match → 304 (sem corpo).
 *
 * @param {{ etag?: string|null }} [opts]
 * @returns {Promise<{ data: object[], dataSource?: string, etag?: string|null }|{ notModified: true, etag?: string|null }|null>}
 */
export async function fetchOpenOrdersGroupsSilent({ etag } = {}) {
  try {
    const headers = authHeaders();
    if (etag) {
      headers['If-None-Match'] = etag;
    }
    // poll=1: marca o uso (logs/futuro early-exit); ETag vale para qualquer caller
    const res = await fetch(`${API_BASE_URL}/api/orders/open?poll=1`, {
      headers,
    });
    if (res.status === 304) {
      return { notModified: true, etag: etag || res.headers.get('ETag') };
    }
    if (!res.ok) return null;
    const json = await res.json().catch(() => null);
    if (!json?.success || !Array.isArray(json.data)) return null;
    return {
      data: json.data,
      dataSource: json.dataSource,
      etag: res.headers.get('ETag') || null,
    };
  } catch {
    return null;
  }
}

/** Assinatura estável (OS + status) para evitar setState quando nada mudou. */
export function fingerprintOpenOrderGroups(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return '';
  const parts = [];
  for (const group of groups) {
    const ordens = group?.ordens;
    if (!Array.isArray(ordens)) continue;
    for (const ordem of ordens) {
      const id = String(ordem?.TB02115_CODIGO ?? ordem?.id ?? '').trim();
      if (!id) continue;
      const st = normalizeOsStatusCod(ordem?.TB02115_STATUS);
      parts.push(`${id}:${st}`);
    }
  }
  if (!parts.length) return '';
  parts.sort();
  return parts.join('|');
}

/** Lista OS Em aberto no status informado (mesmas do badge). */
export function listOpenOrdersByStatus(openOrderGroups, statusCod) {
  const ref = normalizeOsStatusCod(statusCod);
  if (ref.length !== 2 || !Array.isArray(openOrderGroups)) return [];
  const rows = [];
  for (const group of openOrderGroups) {
    const ordens = group?.ordens;
    if (!Array.isArray(ordens)) continue;
    for (const ordem of ordens) {
      if (normalizeOsStatusCod(ordem?.TB02115_STATUS) !== ref) continue;
      const id = String(ordem?.TB02115_CODIGO ?? ordem?.id ?? '').trim();
      if (!id) continue;
      rows.push({
        id,
        cliente: getBoardClienteExibicao(ordem) || '—',
      });
    }
  }
  return rows;
}

/** Nome do status (TB01073_NOME) usado pelo filtro da coluna Em aberto. */
export function resolveOpenStatusNome(openOrderGroups, statusCod) {
  const ref = normalizeOsStatusCod(statusCod);
  if (ref.length !== 2 || !Array.isArray(openOrderGroups)) return '';
  for (const group of openOrderGroups) {
    const ordens = group?.ordens;
    if (!Array.isArray(ordens)) continue;
    for (const ordem of ordens) {
      if (normalizeOsStatusCod(ordem?.TB02115_STATUS) !== ref) continue;
      const nome = String(ordem?.TB01073_NOME ?? '').trim();
      if (nome) return nome;
    }
  }
  return '';
}

/** Conta OS da coluna Em aberto no status informado (dados já carregados no board). */
export function countOpenOrdersByStatus(openOrderGroups, statusCod) {
  return listOpenOrdersByStatus(openOrderGroups, statusCod).length;
}

export function invalidateOsStatusInicialCache() {
  cachedAt = 0;
}
